<template>
  <div class="dashboard-token-address-wrapper">
    <div class="dashboard-token-address">
      <div class="card">
        <el-collapse>
          <el-tooltip popper-class="popper-default" placement="bottom" :auto-close="10000">
            <template #content>
              <p
                v-html="
                  address
                    ? $t('appStatisticTokenAddressTooltip', { walletName: walletInfo?.name })
                    : $t('appStatisticTokenAddressTooltip2')
                "
              />
            </template>
            <span class="tooltip-trigger-position"><Info /></span>
          </el-tooltip>
          <el-collapse-item :title="$t('appStatisticTokenAddressTitle')">
            <hr />

            <div class="buttons-container">
              <div class="button-group">
                <div class="token-name-icon">
                  <img class="icon-image" :src="tokens[tokenData.name.contract as Tokens].toString()" />
                  <span class="token-name">{{ tokenData.methodName }}</span>
                </div>

                <div class="button-group-actions">
                  <UICopyButton v-if="address" :copy-value="tokenAddress" />

                  <el-tooltip
                    :popper-class="{
                      'popper-default': true,
                      'ru-locale': $i18n.locale === 'ru'
                    }"
                    placement="top"
                    trigger="hover"
                    :auto-close="1000"
                  >
                    <template #content>{{ $t('coreViewOnBscscan') }}</template>

                    <a :href="bscScanTokenAddressLink" target="_blank"><LinkNewTab /></a>
                  </el-tooltip>
                </div>

                <div v-if="walletInfo && walletInfo.name && address" class="add-token-to-wallet-button-wrapper">
                  <el-button class="add-token-to-wallet-button">
                    <span @click="addTokenToWalletHandler(blockchain.contracts[store.currentToken].addresses.contract)">
                      {{ $t('appStatisticAddTokenToWallet', { walletName: walletInfo.name }) }}
                    </span>
                  </el-button>
                </div>
              </div>

              <div
                v-if="
                  blockchain.contracts[store.currentToken].addresses.secondaryToken &&
                  tokenData?.extraReward &&
                  tokenData.label !== tokenData?.extraReward.label
                "
                class="button-group secondary"
              >
                <div class="token-name-icon">
                  <img
                    class="icon-image"
                    :src="
                      tokens[
                        tokenMetaData(
                          blockchain.contracts,
                          blockchain.contracts[store.currentToken].addresses.secondaryToken as string
                        ).name.contract as Tokens
                      ].toString()
                    "
                  />
                  <span class="token-name">{{
                    tokenMetaData(
                      blockchain.contracts,
                      blockchain.contracts[store.currentToken].addresses.secondaryToken as string
                    ).methodName
                  }}</span>
                </div>

                <div class="button-group-actions">
                  <UICopyButton v-if="address" :copy-value="secondaryTokenAddress" />

                  <el-tooltip
                    :popper-class="{
                      'popper-default': true,
                      'ru-locale': $i18n.locale === 'ru'
                    }"
                    placement="top"
                    trigger="hover"
                    :auto-close="1000"
                  >
                    <template #content>{{ $t('coreViewOnBscscan') }}</template>

                    <a :href="bscScanSecondaryTokenAddressLink" target="_blank"><LinkNewTab /></a>
                  </el-tooltip>
                </div>

                <div v-if="walletInfo && walletInfo.name && address" class="add-token-to-wallet-button-wrapper">
                  <el-button class="add-token-to-wallet-button">
                    <span
                      @click="
                        () =>
                          addTokenToWalletHandler(
                            blockchain.contracts[store.currentToken].addresses.secondaryToken as string
                          )
                      "
                    >
                      {{ $t('appStatisticAddTokenToWallet', { walletName: walletInfo.name }) }}
                    </span>
                  </el-button>
                </div>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Info from '~/components/icons/Info.vue';
import { useWalletInfo, useWeb3ModalAccount, useWeb3ModalProvider } from '@web3modal/ethers/vue';
import tokens from 'public/img/tokens';
import { Tokens, tokensConfig } from '~/utils/constants';
import LinkNewTab from '~/components/icons/LinkNewTab.vue';
import UICopyButton from '~/components/UI/UICopyButton.vue';

const { walletProvider } = useWeb3ModalProvider();
const { address } = useWeb3ModalAccount();
const { addTokenToWallet } = useWalletMethods();
const { walletInfo } = useWalletInfo();
const { blockchain } = useEnvs();
const store = useMainStore();
const tokenAddress = computed(() => blockchain.contracts[store.currentToken].addresses.contract);
const secondaryTokenAddress = computed(
  () => blockchain.contracts[store.currentToken].addresses.secondaryToken as string
);

const tokenData = computed(() => tokensConfig[store.currentToken]);

const bscScanTokenAddressLink = computed(() => blockchain.blockScanUrl + '/address/' + tokenAddress.value);
const bscScanSecondaryTokenAddressLink = computed(
  () => blockchain.blockScanUrl + '/address/' + secondaryTokenAddress.value
);

const addTokenToWalletHandler = useDebounceFn(async (tokenAddress: string): Promise<void> => {
  if (!walletProvider) {
    return;
  }

  const options = {
    address: tokenAddress,
    decimals: 18,
    image: ''
  };

  await addTokenToWallet(options);
}, 1000);
</script>

<style scoped lang="scss">
.dashboard-token-address {
  hr {
    height: 2px;
    border: none;
    margin-top: 8px;
    margin-bottom: 24px;
    background-color: #15333e;
  }
  .el-collapse {
    margin: 7px 20px;
  }
}

.tooltip-trigger-position {
  position: absolute;
  top: 14px;
  right: 0px;
}

.add-token-to-wallet-button {
  cursor: pointer;
  outline-offset: 0px;
  color: #fff;
  text-align: center;
  transform-style: preserve-3d;
  background-color: rgba(0, 0, 0, 0);
  border: 2px solid #133442;
  border-radius: 48px;
  outline: 1px #c49a4e;
  font-family: Eczar, sans-serif;
  font-weight: 600;
  transition:
    border-color 0.3s,
    transform 0.3s,
    color 0.3s;
  transform: translate(0);
  padding: 14px 24px;
  font-size: 20px;
  line-height: 1.125em;
  height: auto;

  &:hover {
    opacity: 0.6;
    transform: scale3d(0.96, 0.96, 1.01);
  }

  @media screen and (max-width: 500px) {
    margin-top: 20px;
  }

  &.add-logic {
    @media screen and (max-width: 500px) {
      display: none;
    }
  }
}

.add-token-to-wallet-button-wrapper {
  min-width: 270px;

  button {
    max-height: 50px;
  }
}

.buttons-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);

  .button-group.secondary {
    justify-self: end;
  }

  @media screen and (max-width: 1200px) {
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: 15px;

    .button-group.secondary {
      justify-self: start;
    }
  }

  .button-group {
    display: flex;
    min-height: 50px;
  }

  .button-group-actions {
    display: flex;
    gap: 4px;
    align-items: center;
    padding: 0 28px 0 16px;
  }

  .icon-image {
    height: 40px;
    width: 40px;
  }

  .token-name-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;

    .token-name {
      font-family: Grenze;
      font-weight: 600;
      font-size: 30px;
      line-height: 1;
      color: var(--main-text-color);
      white-space: nowrap;
    }
  }
}
</style>

<style lang="scss">
.dashboard-token-address {
  .el-collapse-item__header {
    justify-content: center;
    font-family: Grenze;
    font-weight: 700;
    font-size: 32px;
    line-height: 1;
    text-align: center;
    color: var(--main-text-color);
    background-color: transparent;
  }

  .el-collapse {
    .el-collapse-item__title {
      text-align: center;
    }

    .el-collapse-item__arrow {
      right: 40px;
    }
  }
}

.dashboard-token-address-wrapper {
  margin-bottom: 24px;
}
</style>
